import { type IRemoteNotification, useNotifications } from '@magicbell/react-headless';
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateIcon,
    List,
    type ListRenderItemInfo,
    ListSkeleton,
    LoadingPanel,
} from '@gitbook/spine-react';

import { NotificationItem } from './NotificationItem';

/**
 * Panel listing notifications for the current user.
 */
export function NotificationsPanel(): React.ReactElement {
    const allNotifications = useNotifications('all');

    const onPressMarkAllAsRead = () => {
        analytics.logEvent({
            type: 'notification_mark_all_as_read',
        });

        allNotifications?.markAllAsRead();
    };

    return (
        <div className="flex h-full max-h-full w-auto flex-col md:size-[400px]">
            <div className="flex items-center gap-2 border-b border-b-base px-3 py-2 pr-11 sm:px-5 sm:pr-5">
                <div className="flex-1 font-bold text-ui-heading-small">Notifications</div>
                <Button
                    iconOnly
                    leadingIcon={Icon.DoubleCheck}
                    kind="blank"
                    size="medium"
                    onPress={onPressMarkAllAsRead}
                    label="Mark all as read"
                />
                <Button
                    iconOnly
                    leadingIcon={Icon.Settings}
                    kind="blank"
                    size="medium"
                    label="Notifications settings"
                    href={{
                        current: {
                            type: 'account',
                            tab: 'notification',
                        },
                    }}
                />
            </div>
            <React.Suspense
                fallback={
                    <div className="flex-1">
                        <LoadingPanel size="large" />
                    </div>
                }
            >
                <NotificationsList />
            </React.Suspense>
        </div>
    );
}

/**
 * A list of all notifications.
 */
function NotificationsList() {
    const notifications = useNotifications('all');

    const renderItem = React.useCallback((itemProps: ListRenderItemInfo<IRemoteNotification>) => {
        return <NotificationItem notification={itemProps.item} />;
    }, []);

    const onLoadNextPage = async () => {
        return notifications?.fetchNextPage();
    };

    if (!notifications || !notifications.lastFetchedAt) {
        // loading the initial notifications set
        return <ListSkeleton className="px-2" />;
    }

    if (notifications.isEmpty) {
        return (
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.Bell />
                </EmptyStateIcon>
                <Description>Your GitBook updates will appear here!</Description>
            </EmptyState>
        );
    }

    return (
        <List
            items={notifications.notifications}
            renderItem={renderItem}
            className="flex-1 overflow-y-auto p-3"
            footer={
                notifications?.hasNextPage ? (
                    <div className="flex flex-col pt-4">
                        <Button
                            kind="secondary"
                            leadingIcon={Icon.ArrowDown}
                            onPress={onLoadNextPage}
                            label="Load more"
                        />
                    </div>
                ) : null
            }
        />
    );
}
